import $ from 'jquery';
import UI from '~/ui';
import { getApi, handleError } from '~/auth';
import Component from '~/components/Component';
import { t } from '~/hooks/useI18n';

/**
 * Interfaccia per la risposta dell'API di iscrizione newsletter
 */
interface NewsletterResponse {
    success: boolean;
    message: string;
}

/**
 * Component per la gestione dell'iscrizione alla newsletter
 */
export class Newsletter extends Component {
    private emailInput: HTMLInputElement | null = null;
    private submitButton: HTMLButtonElement | null = null;
    private errorElement: HTMLElement | null = null;
    private isSubmitting: boolean = false;
    private isInitialized: boolean = false;

    constructor(
        formSelector: string = "#newsletterForm",
        private listId: number = (window as any).ATP_ListID_Newsletter,
        private groupId: number = (window as any).ATP_GroupId_Newsletter
    ) {
        super(formSelector, true);
    }

    /**
     * Inizializza gli event listeners
     */
    private initialize(): void {
        if (!this.emailInput || !this.submitButton) {
            return;
        }

        this.submitButton.addEventListener("click", async (event) => {
            event.preventDefault();
            await this.handleSubmit();
        });

        // Gestisce l'invio con il tasto Enter
        this.emailInput.addEventListener("keypress", async (event) => {
            if (event.key === "Enter") {
                event.preventDefault();
                await this.handleSubmit();
            }
        });

        // Nascondi l'errore quando l'utente inizia a digitare
        this.emailInput.addEventListener("input", () => {
            this.hideError();
        });

        this.isInitialized = true;
    }

    /**
     * Gestisce l'invio del form
     */
    private async handleSubmit(): Promise<void> {
        // Previene invii multipli
        if (this.isSubmitting) {
            return;
        }

        if (!this.validateEmail()) {
            return;
        }

        try {
            this.isSubmitting = true;
            this.disableButton();
            await this.iscrizioneNewsletter();
        } finally {
            this.isSubmitting = false;
            this.enableButton();
        }
    }

    /**
     * Valida l'email inserita
     */
    private validateEmail(): boolean {
        if (!this.emailInput) {
            return false;
        }

        const email = this.emailInput.value?.trim();

        if (!email) {
            this.showError(t("validation.email"));
            return false;
        }

        // Validazione email con regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            this.showError(t("validation.emailInvalidFormat"));
            return false;
        }

        this.hideError();
        return true;
    }

    /**
     * Effettua l'iscrizione alla newsletter
     */
    private async iscrizioneNewsletter(): Promise<void> {
        if (!this.emailInput) {
            return;
        }

        const email = this.emailInput.value.trim();

        try {
            UI.showWait();

            const url = `${window.BaseUrlWebApi}MailUp/IscrizioneGruppoWithConfirmation`;
            const queryParams = `idLista=${this.listId}&groupId=${this.groupId}&email=${encodeURIComponent(email)}`;

            const result = await getApi().fetchWithAuth(`GET ${url}?${queryParams}`) as string;

            if (!result.startsWith("Errore")) {
                const message = t("messages.newsletterEmailSent", { email });

                await UI.confirmOneButton("", message);
                this.resetForm();
            } else {
                const errorMessage = t("messages.newsletterAlreadySubscribed", { email });
                await UI.confirmOneButton("", errorMessage);
            }

        } catch (error) {
            const errorMessage = t("messages.newsletterSubscriptionError");
            UI.confirmOneButton("", handleError(error) || errorMessage);
            console.error("Errore iscrizione newsletter:", error);
        } finally {
            UI.hideWait();
        }
    }

    /**
     * Mostra il messaggio di errore
     */
    private showError(message: string): void {
        if (this.errorElement) {
            this.errorElement.textContent = message;
            this.errorElement.style.display = "block";
            $(this.errorElement).removeClass("d-none").show();
        }
    }

    /**
     * Nasconde il messaggio di errore
     */
    private hideError(): void {
        if (this.errorElement) {
            this.errorElement.style.display = "none";
            $(this.errorElement).addClass("d-none").hide();
        }
    }

    /**
     * Disabilita il pulsante durante l'invio
     */
    private disableButton(): void {
        if (this.submitButton) {
            this.submitButton.disabled = true;
            const originalText = this.submitButton.innerHTML;
            this.submitButton.dataset.originalText = originalText;
            this.submitButton.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>${t("common.sending")}`;
        }
    }

    /**
     * Riabilita il pulsante dopo l'invio
     */
    private enableButton(): void {
        if (this.submitButton) {
            this.submitButton.disabled = false;
            const originalText = this.submitButton.dataset.originalText || t("common.subscribe");
            this.submitButton.innerHTML = originalText;
        }
    }

    /**
     * Reset del form dopo l'iscrizione
     */
    private resetForm(): void {
        if (this.emailInput) {
            this.emailInput.value = "";
        }
        this.hideError();
    }

    /**
     * Metodo pubblico per attivare il componente
     */
    public async activate(): Promise<void> {
        // Verifica che il form principale esista
        if (!this.domElement) {
            console.log("Newsletter: form non presente nella pagina");
            return;
        }

        // Evita inizializzazioni multiple
        if (this.isInitialized) {
            console.log("Newsletter component già attivato");
            return;
        }

        // Recupera gli elementi dal DOM
        this.emailInput = document.getElementById("txtEmailNewsletter") as HTMLInputElement;
        this.submitButton = document.getElementById("btnNewsletter") as HTMLButtonElement;
        this.errorElement = document.getElementById("news-error") as HTMLElement;

        if (!this.emailInput || !this.submitButton) {
            console.warn("Newsletter: elementi del form non trovati. Il componente non verrà inizializzato.");
            return;
        }

        // Inizializza gli event listeners
        this.initialize();

        console.log("Newsletter component attivato");
    }
}

// Export default per compatibilità
export default Newsletter;
